import { getFromStorage, removeFromStorage } from '@vend/utilities'

const API_SAMPLE_SENTIMENT = '/api/2.0/sentiment/sample/activity'
const MODULE_NAME = 'Sentiment'

/**
 * Service for registering activity and saving user feedback for various activities.
 */
export default class FeedbackService {
  constructor($http, vdTracking) {
    'ngInject'
    this.$http = $http
    this.vdTracking = vdTracking
  }

  /**
   * Register that the current user performed a specific activity.
   *
   * @method registerActivity
   *
   * @param {String} activity
   * @param {Boolean} forceUnseenActivitySample
   *
   * @return {Promise<Object>} A promise that resolves with details about the registered activity including a property
   *         `requestSentiment` that when true indicates the user should be requested for feedback on the activity
   */
  registerActivity(activity, forceUnseenActivitySample) {
    return this.$http
      .post(API_SAMPLE_SENTIMENT, {
        activity_name: activity,
        force_unseen_activity_sample: Boolean(forceUnseenActivitySample),
      })
      .then(response => response.data.data)
      .then(data => {
        const hasResponded = this._hasCachedResponseForActivity(activity)
        const requestSentiment = data.request_sentiment && !hasResponded

        return {
          requestSentiment,
        }
      })
  }

  /**
   * Notify that the feedback banner was displayed to guage sentiment for the specified activity.
   *
   * @method sentimentRequested
   *
   * @param {Object} options
   *        @param {String} options.activity
   *
   * @return {Promise}
   */
  sentimentRequested({ activity }) {
    return this.vdTracking.trackUserEvent({
      moduleName: MODULE_NAME,
      eventName: 'Displayed',
      metadata: {
        activity,
      },
    })
  }

  /**
   * Save user feedback for the specified activity.
   *
   * @method saveFeedback
   *
   * @param {Object} options
   *        @param {String} options.activity
   *        @param {Number} options.score
   *
   * @return {Promise}
   */
  saveFeedback({ activity, score }) {
    return this.vdTracking.trackUserEvent({
      moduleName: MODULE_NAME,
      eventName: 'Response',
      metadata: {
        activity,
        score,
      },
    })
  }

  /**
   * Save additional details for feedback on a specified activity (further comments to give context to a negative
   * sentiment).
   *
   * @method saveAdditionalDetails
   *
   * @param {Object} options
   *        @param {String} options.activity
   *        @param {Number} options.score
   *        @param {String} options.comments
   *
   * @return {Promise}
   */
  saveAdditionalDetails({ activity, score, comments }) {
    return this.vdTracking.trackUserEvent({
      moduleName: MODULE_NAME,
      eventName: 'AdditionalDetails',
      metadata: {
        activity,
        score,
        comments,
      },
    })
  }

  /**
   * Determine whether there is a cached response for this activity in local storage (and clear it if there is).
   *
   * @private
   * @method _hasCachedResponseForActivity
   *
   * @param {String} activity
   *
   * @return {Boolean} true if there was a cached response in local storage, false otherwise
   */
  _hasCachedResponseForActivity(activity) {
    const activityKey = '_vend_feedback_' + activity
    const hasResponded = getFromStorage(activityKey) === 'responded'
    removeFromStorage(activityKey)
    return hasResponded
  }
}
